/**
 * La biblioteca de imágenes y PDF de fondo de las plantillas (F1d): elegir uno,
 * subir uno nuevo o eliminar los que ninguna plantilla usa. El mismo archivo
 * subido dos veces no se duplica (el backend lo reconoce por su huella).
 */
import { useRef, useState } from 'react'
import { FileUp, ImageOff, Trash2 } from 'lucide-react'
import { Button } from '@/components/ds/Button'
import { IconButton } from '@/components/ds/IconButton'
import { Modal } from '@/components/ds/Modal'
import { getApiErrorMessage } from '@/lib/apiClient'
import {
  useAssetUrl,
  useDeletePrintAsset,
  usePrintAssets,
  useUploadPrintAsset,
  type PrintAsset,
  type TipoDeArchivo,
} from '@/modules/config/hooks/usePrintTemplates'

const ACEPTA: Record<TipoDeArchivo, string> = {
  image: 'image/png,image/jpeg,image/webp,image/svg+xml',
  background: 'application/pdf',
}
const AYUDA: Record<TipoDeArchivo, string> = {
  image: 'PNG, JPG, WebP o SVG de hasta 5 MB. Se optimiza al subirla.',
  background: 'PDF de 1 o 2 páginas (la 2.ª va en las páginas siguientes). Mejor bajo 300 KB: se optimiza al subirlo.',
}

const kb = (bytes: number) => `${Math.max(1, Math.round(bytes / 1024))} KB`

/** La miniatura: la imagen misma o la primera página del fondo. */
export function Miniatura({ asset, kind, className = '' }: { asset?: string; kind: TipoDeArchivo; className?: string }) {
  const url = useAssetUrl(asset, kind === 'image' ? 'content' : 'preview')
  if (!asset) return null
  return url
    ? <img src={url} alt="" className={`object-contain bg-white ${className}`} />
    : <span className={`flex items-center justify-center bg-surface-muted text-content-subtle ${className}`}><ImageOff size={14} /></span>
}

function Ficha({ archivo, elegido, onElegir }: { archivo: PrintAsset; elegido: boolean; onElegir: () => void }) {
  const borrar = useDeletePrintAsset()
  const [confirmar, setConfirmar] = useState(false)
  const [error, setError] = useState('')
  return (
    <li className={`rounded-lg border p-2 space-y-1.5 ${elegido ? 'border-primary ring-2 ring-focus-ring' : 'border-divider'}`}>
      <button type="button" onClick={onElegir} className="block w-full text-left space-y-1" aria-pressed={elegido}>
        <Miniatura asset={archivo.id} kind={archivo.kind} className="w-full h-24 rounded border border-divider" />
        <span className="block text-xs text-content truncate" title={archivo.name}>{archivo.name}</span>
        <span className="block text-[11px] text-content-subtle">
          {kb(archivo.size_bytes)}
          {archivo.kind === 'image' && archivo.width_px ? ` · ${archivo.width_px}×${archivo.height_px} px` : ''}
          {archivo.kind === 'background' && archivo.page_count ? ` · ${archivo.page_count} pág.` : ''}
        </span>
      </button>
      {archivo.warnings.map(a => <p key={a} className="text-[11px] text-warning-content">{a}</p>)}
      <div className="flex items-center justify-end gap-1">
        {confirmar ? (
          <>
            <span className="text-[11px] text-content-muted mr-auto">¿Eliminar?</span>
            <Button size="sm" variant="ghost" onClick={() => setConfirmar(false)}>No</Button>
            <Button size="sm" variant="danger" loading={borrar.isPending}
              onClick={() => borrar.mutate(archivo.id, { onError: e => { setError(getApiErrorMessage(e)); setConfirmar(false) } })}>
              Eliminar
            </Button>
          </>
        ) : (
          <IconButton label="Eliminar de la biblioteca" icon={<Trash2 size={13} />} bare onClick={() => { setError(''); setConfirmar(true) }} />
        )}
      </div>
      {error && <p role="alert" className="text-[11px] text-danger-content-soft">{error}</p>}
    </li>
  )
}

/** Elegir un archivo de la biblioteca: muestra el elegido y abre la biblioteca para cambiarlo. */
export function SelectorDeArchivo({ kind, value, onChange, etiqueta }: {
  kind: TipoDeArchivo
  value?: string
  onChange: (id: string | undefined) => void
  etiqueta: string
}) {
  const [abierta, setAbierta] = useState(false)
  const { data: archivos = [], isLoading } = usePrintAssets(kind)
  const subir = useUploadPrintAsset()
  const entrada = useRef<HTMLInputElement>(null)
  const [error, setError] = useState('')
  const actual = archivos.find(a => a.id === value)

  const alElegirArchivo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0]
    e.target.value = ''
    if (!archivo) return
    setError('')
    subir.mutate({ file: archivo, kind }, {
      onSuccess: subido => { onChange(subido.id); setAbierta(false) },
      onError: err => setError(getApiErrorMessage(err)),
    })
  }

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-content-muted">{etiqueta}</p>
      {value ? (
        <div className="flex items-center gap-2">
          <Miniatura asset={value} kind={kind} className="h-12 w-16 rounded border border-divider shrink-0" />
          <span className="text-xs text-content truncate flex-1" title={actual?.name}>{actual?.name ?? 'Archivo elegido'}</span>
        </div>
      ) : (
        <p className="text-xs text-content-subtle">{kind === 'image' ? 'Falta elegir la imagen.' : 'Sin PDF de fondo.'}</p>
      )}
      {actual?.warnings.map(a => <p key={a} className="text-[11px] text-warning-content">{a}</p>)}
      <div className="flex gap-1.5">
        <Button size="sm" variant="secondary" onClick={() => setAbierta(true)}>{value ? 'Cambiar…' : 'Elegir…'}</Button>
        {value && <Button size="sm" variant="ghost" onClick={() => onChange(undefined)}>Quitar</Button>}
      </div>

      <Modal open={abierta} onClose={() => setAbierta(false)} size="lg"
        title={kind === 'image' ? 'Imágenes de las plantillas' : 'PDF de fondo'} subtitle={AYUDA[kind]}
        actions={(
          <>
            <input ref={entrada} type="file" accept={ACEPTA[kind]} className="hidden" onChange={alElegirArchivo}
              aria-label={kind === 'image' ? 'Archivo de imagen' : 'Archivo PDF de fondo'} />
            <Button variant="secondary" onClick={() => setAbierta(false)}>Cerrar</Button>
            <Button icon={<FileUp size={14} />} loading={subir.isPending} onClick={() => entrada.current?.click()}>
              Subir archivo
            </Button>
          </>
        )}>
        {error && <p role="alert" className="text-sm text-danger-content-soft mb-3">{error}</p>}
        {isLoading ? (
          <p className="text-sm text-content-subtle">Cargando…</p>
        ) : archivos.length === 0 ? (
          <p className="text-sm text-content-muted">
            {kind === 'image' ? 'Todavía no hay imágenes: subir el logo o la imagen que va en la plantilla.'
              : 'Todavía no hay PDF de fondo: subir el diseño de la hoja (logos, recuadros) para imprimir encima.'}
          </p>
        ) : (
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {archivos.map(a => (
              <Ficha key={a.id} archivo={a} elegido={a.id === value} onElegir={() => { onChange(a.id); setAbierta(false) }} />
            ))}
          </ul>
        )}
      </Modal>
    </div>
  )
}
